import {
  Component,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AFRICA_PATHS } from './africa-paths.data';

export interface CountryMapData {
  name: string;
  code: string;
  count: number;
  percentage: number;
  color: string;
}

/** Maps country names (from countryIndexData) to ISO 3166-1 alpha-2 codes used in the SVG */
const NAME_TO_ALPHA2: { [name: string]: string } = {
  'South Africa': 'ZA',
  'Kenya': 'KE',
  'Nigeria': 'NG',
  'Algeria': 'DZ',
  'Angola': 'AO',
  'Benin': 'BJ',
  'Botswana': 'BW',
  'Burkina Faso': 'BF',
  'Burundi': 'BI',
  'Cameroon': 'CM',
  'Cape Verde': 'CV',
  'Central African Republic': 'CF',
  'Chad': 'TD',
  'Democratic Republic of the Congo': 'CD',
  'Republic of the Congo': 'CG',
  "Cote d'Ivoire": 'CI',
  'Djibouti': 'DJ',
  'Egypt': 'EG',
  'Equatorial Guinea': 'GQ',
  'Eritrea': 'ER',
  'Eswatini': 'SZ',
  'Ethiopia': 'ET',
  'Gabon': 'GA',
  'Gambia': 'GM',
  'Ghana': 'GH',
  'Guinea': 'GN',
  'Guinea-Bissau': 'GW',
  'Lesotho': 'LS',
  'Liberia': 'LR',
  'Libya': 'LY',
  'Malawi': 'MW',
  'Mali': 'ML',
  'Mauritania': 'MR',
  'Morocco': 'MA',
  'Mozambique': 'MZ',
  'Namibia': 'NA',
  'Niger': 'NE',
  'Rwanda': 'RW',
  'Sao Tome and Principe': 'ST',
  'Senegal': 'SN',
  'Sierra Leone': 'SL',
  'Somalia': 'SO',
  'South Sudan': 'SS',
  'Sudan': 'SD',
  'Tanzania': 'TZ',
  'Togo': 'TG',
  'Tunisia': 'TN',
  'Uganda': 'UG',
  'Western Sahara': 'EH',
  'Zambia': 'ZM',
  'Zimbabwe': 'ZW',
};

@Component({
  selector: 'app-africa-map',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './africa-map.component.html',
  styleUrl: './africa-map.component.scss',
})
export class AfricaMapComponent implements OnChanges {
  /** Country index data from ReportsBaseService.countryIndexData */
  @Input() countryData: { name: string; count: number; percentage: number; color: string }[] = [];

  paths = AFRICA_PATHS;
  private colorMap: Map<string, string> = new Map();

  tooltipVisible = false;
  tooltipX = 0;
  tooltipY = 0;
  tooltipCountry = '';
  tooltipPercentage = 0;
  tooltipCount = 0;

  private readonly DEFAULT_COLOR = '#C5CAD3';
  private readonly HOVER_COLOR = '#A0A8B4';

  private hoveredCode: string | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countryData']) {
      this.buildColorMap();
    }
  }

  private buildColorMap(): void {
    this.colorMap.clear();
    if (!this.countryData) return;

    for (const item of this.countryData) {
      const alpha2 = NAME_TO_ALPHA2[item.name];
      if (alpha2 && item.count > 0) {
        this.colorMap.set(alpha2, item.color);
      }
    }
  }

  getCountryColor(code: string): string {
    if (this.hoveredCode === code) {
      return this.colorMap.has(code)
        ? this.adjustBrightness(this.colorMap.get(code)!, -20)
        : this.HOVER_COLOR;
    }
    return this.colorMap.get(code) || this.DEFAULT_COLOR;
  }

  onCountryHover(code: string, event: MouseEvent): void {
    this.hoveredCode = code;
    const countryInfo = this.getCountryInfo(code);
    if (countryInfo) {
      this.tooltipCountry = countryInfo.name;
      this.tooltipPercentage = countryInfo.percentage;
      this.tooltipCount = countryInfo.count;
    } else {
      this.tooltipCountry = this.getCountryName(code);
      this.tooltipPercentage = 0;
      this.tooltipCount = 0;
    }

    const svgEl = (event.target as SVGElement).closest('svg');
    if (svgEl) {
      const rect = svgEl.getBoundingClientRect();
      this.tooltipX = event.clientX - rect.left + 10;
      this.tooltipY = event.clientY - rect.top - 30;
    }
    this.tooltipVisible = true;
  }

  onCountryMove(event: MouseEvent): void {
    const svgEl = (event.target as SVGElement).closest('svg');
    if (svgEl) {
      const rect = svgEl.getBoundingClientRect();
      this.tooltipX = event.clientX - rect.left + 10;
      this.tooltipY = event.clientY - rect.top - 30;
    }
  }

  onCountryLeave(): void {
    this.hoveredCode = null;
    this.tooltipVisible = false;
  }

  private getCountryInfo(code: string): { name: string; count: number; percentage: number } | null {
    if (!this.countryData) return null;
    for (const item of this.countryData) {
      if (NAME_TO_ALPHA2[item.name] === code) {
        return item;
      }
    }
    return null;
  }

  private getCountryName(code: string): string {
    for (const [name, alpha2] of Object.entries(NAME_TO_ALPHA2)) {
      if (alpha2 === code) return name;
    }
    return code;
  }

  private adjustBrightness(hex: string, amount: number): string {
    // Handle rgba format
    const rgbaMatch = hex.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
      const r = Math.max(0, Math.min(255, parseInt(rgbaMatch[1]) + amount));
      const g = Math.max(0, Math.min(255, parseInt(rgbaMatch[2]) + amount));
      const b = Math.max(0, Math.min(255, parseInt(rgbaMatch[3]) + amount));
      return `rgb(${r}, ${g}, ${b})`;
    }
    // Handle hex format
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + amount));
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
    const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
    return `rgb(${r}, ${g}, ${b})`;
  }
}
